import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { buttonVariants } from '@/components/ui/button-variants'
import { cn } from '@/lib/utils'

interface ErrorPageLayoutProps {
  /** `404`, `403` gibi dekoratif durum kodu; erişilebilir isme katkı vermez. */
  readonly code?: string | undefined
  readonly title: string
  readonly description: string
  /** Dekoratif ikon. */
  readonly icon?: ReactNode
  /** Ana sayfa bağlantısı dışındaki ek yönlendirmeler. */
  readonly action?: ReactNode
}

/**
 * C6 — 404 / 403 / bakım sayfalarının ortak iskeleti (`docs/spec/07-pages-ui.md §0`).
 *
 * Üç sayfanın da aynı dili konuşması için tek yerde durur: aynı hizalama, aynı çıkış yolu.
 * Ana sayfa bağlantısı router `Link`'i değil düz `<a>`: bu ekranlar yönlendirmenin kendisinin
 * çalışmadığı durumlarda (bakım, yetkisiz erişim, eşleşmeyen rota) da çizilir ve tam sayfa
 * yükleme, bozulmuş bir istemci durumundan çıkmanın en güvenilir yoludur.
 */
export function ErrorPageLayout({ code, title, description, icon, action }: ErrorPageLayoutProps) {
  const { t } = useTranslation()

  return (
    <section className="mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center">
      {icon ? (
        <span aria-hidden="true" className="text-muted-foreground [&_svg]:size-10">
          {icon}
        </span>
      ) : null}

      {code === undefined ? null : (
        <span
          aria-hidden="true"
          className="text-muted-foreground font-mono text-sm tracking-widest"
        >
          {code}
        </span>
      )}

      <h1 className="text-foreground text-xl font-semibold">{title}</h1>
      <p className="text-muted-foreground text-sm">{description}</p>

      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        <a href="/" className={cn(buttonVariants({ variant: 'outline' }))}>
          {t('errors.backHome')}
        </a>
        {action}
      </div>
    </section>
  )
}
